import React from 'react';
import { useBrandClassificationSplineMetrics } from './hooks/useBrandClassificationMetrics';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { getAdjustedDataSetsForSpline, getSplineTotals, saveSplineAsDelimitedData } from 'src/utils/chartUtils';
import { calculatePercentChange } from 'src/utils/app';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter
} from 'src/utils/Hooks';
import { useCompanyName } from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { TrafficType } from '../types';
import { brandClassificationResponseToSpline } from '../utils/trafficParsers';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

export interface TrafficSplineChartForTopicsProps {
  trafficType: TrafficType;
}

/**
 * Total traffic chart for topics/topic groups view, uses different
 * endpoints for fetching the metrics
 */
export const TrafficSplineChartForTopics = ({ trafficType }: TrafficSplineChartForTopicsProps) => {
  const { displayName: mainPeriodDisplay } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplay } = useAppSelector((state) => state.comparisonTimePeriod);

  const formatMetric = useMetricFormatter();
  const { response, isLoading } = useBrandClassificationSplineMetrics();

  const rawPrimaryData = brandClassificationResponseToSpline(response[0], trafficType);
  const rawSecondaryData = brandClassificationResponseToSpline(response[1], trafficType);

  const { primaryData, secondaryData } = getAdjustedDataSetsForSpline(rawPrimaryData, rawSecondaryData);
  const { primaryTotal, secondaryTotal } = getSplineTotals(rawPrimaryData, rawSecondaryData, { func: 'sum' });
  const percentChange = calculatePercentChange(primaryTotal, secondaryTotal);

  // const { subtitleDisplayName } = useGlobalSubtitleName({ includeUnnamed: false });
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const companyName = useCompanyName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const trafficTitle = {
    totalTraffic: 'Total Traffic',
    organicTraffic: 'Organic Traffic',
    paidTraffic: 'Paid Traffic'
  }[trafficType];

  const getSubtitle = () => {
    const formattedClicks = formatMetric(primaryTotal, METRICTYPE.VOLUME, { showFullValue: false, decimalPlaces: 1 });
    const formattedPercent = formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 });
    const percentDirection = percentChange >= 0 ? 'higher' : 'lower';

    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`${companyName} earned ${formattedClicks} ${trafficTitle.toLowerCase()} `}
          {taxonomyText}
          {` in ${entityPhrase}`}
          {` during the ${mainPeriodDisplayName} period, which is ${formattedPercent} ${percentDirection} compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconChartWithLegend
      title={trafficTitle}
      loading={isLoading}
      includeSummaryInfo={false}
      primaryLegendProps={{
        metric: formatMetric(primaryTotal, METRICTYPE.VOLUME, { decimalPlaces: 2, showFullValue: false }),
        displayName: mainPeriodDisplay,
        change: formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
        direction: getDirection(percentChange)
      }}
      comparisonLegendProps={{
        metric: formatMetric(secondaryTotal, METRICTYPE.VOLUME, { decimalPlaces: 2, showFullValue: false }),
        displayName: comparisonPeriodDisplay
      }}
      convertSeriesToDelimitedData={async () => saveSplineAsDelimitedData(primaryData, secondaryData, trafficTitle)}
      subtitle={getSubtitle()}
    >
      <SplineChart metricType={METRICTYPE.VOLUME} primaryData={primaryData} secondaryData={secondaryData} />
    </BeaconChartWithLegend>
  );
};
